import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ShoppingBag, Search, User, Menu, X, LogOut, LayoutDashboard, Heart } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import SearchModal from "./SearchModal";

const NAV = [
  { label: "Home", to: "/", mobileOnly: true },
  { label: "Shop", to: "/shop" },
  { label: "Perfumes", to: "/shop?category=Perfumes" },
  { label: "Air Fresheners", to: "/shop?subcategory=Air%20Fresheners" },
  { label: "Skincare", to: "/shop?category=Skincare" },
  { label: "Soaps", to: "/shop?category=Soaps" },
  { label: "Best Sellers", to: "/shop?best=1", wide: true },
  { label: "New Arrivals", to: "/shop?new=1", wide: true },
  { label: "About Us", to: "/about", wide: true },
  { label: "Contact", to: "/contact" },
];

export const Header = () => {
  const { count, setOpen } = useCart();
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); setAccountOpen(false); }, [location]);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 left-0 right-0 z-50 glass transition-all duration-500 ${
          scrolled ? "border-b border-[rgba(212,175,55,0.25)] py-3" : "py-5 border-b border-transparent"
        }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 flex items-center justify-between">
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden text-ivory p-1"
            onClick={() => setMobileOpen(true)}>
            <Menu size={22} className="text-[#FAFAF7]" />
          </button>

          <Link to="/" data-testid="logo-link" className="flex flex-col items-center lg:items-start leading-none">
            <span className="font-serif-lux text-xl sm:text-2xl tracking-wide gold-text font-semibold whitespace-nowrap">QUEEN MJ MAISON BEAUTE</span>
            <span className="hidden sm:block eyebrow text-[0.55rem] mt-0.5 text-[#D4AF37]/70">BEAUTE & WELLNESS</span>
          </Link>

          <nav className="hidden lg:flex items-center gap-5 xl:gap-6 ml-8">
            {NAV.filter((n) => !n.mobileOnly).map((n) => (
              <Link
                key={n.label}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}-link`}
                className={`link-underline text-[0.8rem] tracking-wide text-[#FAFAF7]/85 hover:text-[#D4AF37] transition-colors whitespace-nowrap ${n.wide ? "hidden xl:inline-block" : ""}`}>
                {n.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-3 sm:gap-5">
            <button data-testid="search-trigger" onClick={() => setSearchOpen(true)} className="text-[#FAFAF7]/85 hover:text-[#D4AF37] transition-colors">
              <Search size={19} />
            </button>

            <div className="relative">
              <button
                data-testid="account-trigger"
                onClick={() => (user ? setAccountOpen((v) => !v) : navigate("/login"))}
                className="text-[#FAFAF7]/85 hover:text-[#D4AF37] transition-colors">
                <User size={19} />
              </button>
              {user && accountOpen && (
                <div data-testid="account-menu" className="absolute right-0 mt-3 w-52 glass gold-border rounded-lg py-2 shadow-2xl">
                  <div className="px-4 py-2 border-b border-[rgba(212,175,55,0.15)]">
                    <p className="text-sm text-[#FAFAF7] truncate">{user.name}</p>
                    <p className="text-xs text-[#FAFAF7]/50 truncate">{user.email}</p>
                  </div>
                  <Link to="/account" data-testid="menu-account-link" className="flex items-center gap-2 px-4 py-2 text-sm text-[#FAFAF7]/80 hover:text-[#D4AF37] hover:bg-white/5">
                    <User size={15} /> My Account
                  </Link>
                  <Link to="/account?tab=wishlist" className="flex items-center gap-2 px-4 py-2 text-sm text-[#FAFAF7]/80 hover:text-[#D4AF37] hover:bg-white/5">
                    <Heart size={15} /> Wishlist
                  </Link>
                  {user.role === "admin" && (
                    <Link to="/admin" data-testid="menu-admin-link" className="flex items-center gap-2 px-4 py-2 text-sm text-[#D4AF37] hover:bg-white/5">
                      <LayoutDashboard size={15} /> Admin Dashboard
                    </Link>
                  )}
                  <button data-testid="logout-button" onClick={() => { logout(); navigate("/"); }} className="w-full flex items-center gap-2 px-4 py-2 text-sm text-[#FAFAF7]/80 hover:text-[#D4AF37] hover:bg-white/5">
                    <LogOut size={15} /> Sign Out
                  </button>
                </div>
              )}
            </div>

            <button data-testid="cart-trigger" onClick={() => setOpen(true)} className="relative text-[#FAFAF7]/85 hover:text-[#D4AF37] transition-colors">
              <ShoppingBag size={19} />
              {count > 0 && (
                <span data-testid="cart-count-badge" className="absolute -top-2 -right-2 bg-[#D4AF37] text-[#0B0B0C] text-[0.6rem] font-semibold w-4 h-4 rounded-full flex items-center justify-center">
                  {count}
                </span>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <div className="absolute inset-0 bg-black/70" onClick={() => setMobileOpen(false)} />
          <div className="absolute left-0 top-0 bottom-0 w-72 bg-[#0B0B0C] gold-border border-r p-6 fade-up overflow-y-auto">
            <div className="flex justify-between items-center mb-8">
              <span className="font-serif-lux text-xl gold-text">QUEEN MJ MAISON BEAUTE</span>
              <button data-testid="mobile-menu-close" onClick={() => setMobileOpen(false)}><X size={20} className="text-[#FAFAF7]" /></button>
            </div>
            <nav className="flex flex-col gap-1">
              {NAV.map((n) => (
                <Link key={n.label} to={n.to} data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="py-3 border-b border-[rgba(212,175,55,0.12)] text-[#FAFAF7]/85 hover:text-[#D4AF37]">
                  {n.label}
                </Link>
              ))}
              {!user && <Link to="/login" className="py-3 text-[#D4AF37]">Sign In</Link>}
            </nav>
          </div>
        </div>
      )}

      <SearchModal open={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
};
